import { Box, Flex, Icon, List, ListItem, Text, useTheme, Divider } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { FaHome, FaPalette, FaBullhorn, FaListAlt, FaFilter, FaLink } from 'react-icons/fa';
import { IconType } from 'react-icons';
import AdminRouteUserProfile from 'src/components/atoms/admin-route-user-profile';
import AdminRouteMarketplaceProfile from 'src/components/atoms/admin-route-marketplace-profile';

interface AdminRouteSidebarProps {
  currentPage: string;
  onNavigate?: () => void;
}

type NavItem = {
  label: string;
  href: string;
  icon: IconType;
  pageKey: string;
};

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Home',
    href: '/',
    icon: FaHome,
    pageKey: 'home',
  },
  {
    label: 'Customize',
    href: '/customize',
    icon: FaPalette,
    pageKey: 'customize',
  },
  {
    label: 'Refine Your Niche',
    href: '/refine-niche',
    icon: FaFilter,
    pageKey: 'refine-niche',
  },
  {
    label: 'Curate Listings',
    href: '/curate-listings',
    icon: FaListAlt,
    pageKey: 'curate-listings',
  },
  {
    label: 'Social Connect',
    href: '/social-connect',
    icon: FaLink,
    pageKey: 'social-connect',
  },
  {
    label: 'Social Automations',
    href: '/social-automations',
    icon: FaBullhorn,
    pageKey: 'social-automations',
  },
];

const AdminRouteSidebar = ({ currentPage, onNavigate }: AdminRouteSidebarProps) => {
  const router = useRouter();
  const theme = useTheme();

  const bgColor = theme.colors.navBG ?? 'white';
  const textColor = theme.colors.navText ?? 'black';
  const activeBg = theme.colors.gray?.[100] ?? '#f4f6f8';
  const hoverBg = theme.colors.gray?.[50] ?? '#fafbfc';

  const isActive = (item: NavItem): boolean => {
    if (item.pageKey === 'home') return currentPage === '' || currentPage === 'home';
    return currentPage === item.pageKey;
  };

  return (
    <Box
      as="nav"
      bg={bgColor}
      color={textColor}
      width="260px"
      borderRight="1px solid"
      borderColor="gray.100"
      pt={3}
      pb={4}
      px={3}
      position={{ base: 'relative', lg: 'sticky' }}
      top={{ lg: 0 }}
      height={{ base: '100vh', lg: '100vh' }}
      display="flex"
      flexDirection="column"
    >
      <Box mt="auto">
        <Box px={2} pb={4}>
          <AdminRouteMarketplaceProfile />
        </Box>
      </Box>

      <List spacing={1} flex="1">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item);
          return (
            <ListItem key={item.href}>
              <Flex
                align="center"
                gap={3}
                px={3}
                py={1}
                borderRadius="md"
                bg={active ? activeBg : 'transparent'}
                cursor="pointer"
                onClick={() => {
                  router.push(item.href);
                  onNavigate?.();
                }}
                _hover={{ bg: active ? activeBg : hoverBg }}
                transition="background-color 120ms ease"
              >
                <Icon as={item.icon} boxSize="4" color={active ? 'blue.500' : textColor} />
                <Text fontWeight="normal" noOfLines={1} userSelect="none">
                  {item.label}
                </Text>
              </Flex>
            </ListItem>
          );
        })}
      </List>

      <Box mt="auto" pt={4}>
        <Divider mb={4} borderColor="gray.200" mx={-3} width="calc(100% + 24px)" />
        <Box px={2}>
          <AdminRouteUserProfile onNavigate={onNavigate} />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminRouteSidebar;
